/**
 * Zalo QR Login Component
 * Hiển thị QR code để đăng nhập Zalo và polling trạng thái
 */
import React, { useState, useEffect, useRef } from 'react';
import { fetchData } from '../../../lib/apis';
import { notification, Button, Spin } from 'antd';
import {
    CheckCircleFilled,
    CloseCircleFilled,
    LockOutlined,
    MobileOutlined,
    QrcodeOutlined,
    ReloadOutlined,
    SyncOutlined
} from '@ant-design/icons';
import './ZaloConnect.css';

const ZaloConnect = ({ onSuccess, onCancel, companyId, reconnectChannelId, reconnectChannelName }) => {
    const [step, setStep] = useState(0); // 0: init, 1: waiting, 2: success, 3: error
    const [qrUrl, setQrUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [channelData, setChannelData] = useState(null);
    const pollingRef = useRef(null);
    const sessionIdRef = useRef(null);

    // Khởi tạo QR session
    const initQRLogin = async () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        if (sessionIdRef.current) {
            fetchData({
                url: `api/v1/zalo/login/cancel/${sessionIdRef.current}`,
                method: 'DELETE'
            }).catch(() => {});
            sessionIdRef.current = null;
        }

        setLoading(true);
        setError(null);
        setQrUrl(null);
        setStep(0);

        try {
            const response = await fetchData({
                url: 'api/v1/zalo/login/init',
                method: 'POST',
                data: { company_id: companyId, channel_id: reconnectChannelId || undefined }
            });

            if (response.code === 200) {
                sessionIdRef.current = response.data.sessionId;
                setQrUrl(response.data.qrBase64);
                setStep(1);
                startPolling(response.data.sessionId);
            } else {
                throw new Error(response.message || 'Không thể khởi tạo QR');
            }
        } catch (err) {
            setError(err.message || 'Lỗi kết nối server');
            setStep(3);
        } finally {
            setLoading(false);
        }
    };

    // Polling trạng thái login
    const startPolling = (sid) => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
        }

        pollingRef.current = setInterval(async () => {
            try {
                const response = await fetchData({
                    url: `api/v1/zalo/login/status/${sid}`,
                    method: 'GET'
                });

                if (response.data.status === 'success') {
                    clearInterval(pollingRef.current);
                    await confirmLogin(sid);
                } else if (response.data.status === 'expired' || response.data.status === 'error') {
                    clearInterval(pollingRef.current);
                    setError(response.data.error || 'Mã QR đã hết hạn');
                    setStep(3);
                }
            } catch (err) {
                console.error('Polling error:', err);
            }
        }, 2000);
    };

    // Xác nhận và lưu credentials
    const confirmLogin = async (sid) => {
        setLoading(true);
        try {
            const response = await fetchData({
                url: 'api/v1/zalo/login/confirm',
                method: 'POST',
                data: {
                    sessionId: sid,
                    company_id: companyId,
                    channel_id: reconnectChannelId || undefined
                }
            });

            if (response.code === 200) {
                sessionIdRef.current = null;
                setChannelData(response.data);
                setStep(2);
                notification.success({
                    message: 'Đã kết nối Zalo',
                    description: 'Tài khoản đã sẵn sàng nhận và gửi tin nhắn.'
                });
                if (onSuccess) {
                    onSuccess(response.data);
                }
            } else {
                throw new Error(response.message);
            }
        } catch (err) {
            setError(err.message || 'Lỗi xác nhận đăng nhập');
            setStep(3);
        } finally {
            setLoading(false);
        }
    };

    // Hủy session khi unmount
    useEffect(() => {
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
            if (sessionIdRef.current) {
                fetchData({
                    url: `api/v1/zalo/login/cancel/${sessionIdRef.current}`,
                    method: 'DELETE'
                }).catch(() => {});
            }
        };
    }, []);

    // Auto init khi mount
    useEffect(() => {
        initQRLogin();
    }, []);

    const renderProgress = () => {
        const currentStep = step === 0 ? 0 : step === 2 ? 2 : 1;
        const progressItems = [
            { label: 'Tạo mã', icon: <QrcodeOutlined /> },
            { label: 'Quét mã', icon: <MobileOutlined /> },
            { label: 'Hoàn tất', icon: <CheckCircleFilled /> }
        ];

        return (
            <div className="zalo-connect-progress" aria-label="Tiến trình kết nối Zalo">
                {progressItems.map((item, index) => {
                    const isComplete = index < currentStep || step === 2;
                    const isActive = index === currentStep && step !== 2;
                    const isError = step === 3 && index === currentStep;
                    return (
                        <React.Fragment key={item.label}>
                            {index > 0 && <span className={`zalo-connect-progress__line ${isComplete ? 'is-complete' : ''}`} />}
                            <div className={`zalo-connect-progress__item ${isComplete ? 'is-complete' : ''} ${isActive ? 'is-active' : ''} ${isError ? 'is-error' : ''}`}>
                                <span className="zalo-connect-progress__icon">
                                    {isError ? <CloseCircleFilled /> : isComplete ? <CheckCircleFilled /> : item.icon}
                                </span>
                                <span>{item.label}</span>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    const renderWaiting = () => (
        <div className="zalo-connect-grid">
            <aside className="zalo-connect-guide">
                <span className="zalo-connect-section-label">Thực hiện trên điện thoại</span>
                <h3>Quét mã bằng ứng dụng Zalo</h3>
                <ol className="zalo-connect-guide__steps">
                    <li>
                        <span>1</span>
                        <div><strong>Mở ứng dụng Zalo</strong><small>Đăng nhập đúng tài khoản bạn muốn kết nối.</small></div>
                    </li>
                    <li>
                        <span>2</span>
                        <div><strong>Mở trình quét QR</strong><small>Nhấn biểu tượng QR ở góc trên màn hình Zalo.</small></div>
                    </li>
                    <li>
                        <span>3</span>
                        <div><strong>Quét và xác nhận</strong><small>Giữ màn hình này mở trong lúc đăng nhập.</small></div>
                    </li>
                </ol>
                <div className="zalo-connect-security-note">
                    <LockOutlined />
                    <span>Mã chỉ dùng một lần và không hiển thị mật khẩu Zalo.</span>
                </div>
            </aside>

            <section className="zalo-connect-qr-panel" aria-live="polite">
                <div className="zalo-connect-qr-panel__topline">
                    <span>Mã đăng nhập</span>
                    <span className="zalo-connect-live-badge"><i /> Đang hoạt động</span>
                </div>
                <div className="zalo-connect-qr-frame">
                    {qrUrl ? (
                        <img
                            src={qrUrl}
                            alt="Mã QR đăng nhập tài khoản Zalo"
                            onError={() => {
                                setError('Không thể tải mã QR');
                                setStep(3);
                            }}
                        />
                    ) : (
                        <Spin size="large" />
                    )}
                </div>
                <div className="zalo-connect-waiting">
                    <SyncOutlined spin />
                    <div>
                        <strong>{loading ? 'Đang hoàn tất kết nối' : 'Đang chờ quét mã'}</strong>
                        <span>{loading ? 'Vui lòng giữ nguyên màn hình này.' : 'Hệ thống sẽ tự nhận diện sau khi bạn xác nhận.'}</span>
                    </div>
                </div>
                <Button
                    className="zalo-connect-refresh"
                    icon={<ReloadOutlined />}
                    onClick={initQRLogin}
                    disabled={loading}
                >
                    Tạo mã QR mới
                </Button>
            </section>
        </div>
    );

    const renderStepContent = () => {
        if (step === 0) {
            return (
                <div className="zalo-connect-state" aria-live="polite">
                    <div className="zalo-connect-state__icon is-loading"><Spin /></div>
                    <h3>Đang tạo mã đăng nhập</h3>
                    <p>Quá trình này thường chỉ mất vài giây.</p>
                </div>
            );
        }

        if (step === 1) return renderWaiting();

        if (step === 2) {
            return (
                <div className="zalo-connect-state is-success" aria-live="polite">
                    <div className="zalo-connect-state__icon"><CheckCircleFilled /></div>
                    <span className="zalo-connect-section-label">Kết nối hoàn tất</span>
                    <h3>Tài khoản Zalo đã sẵn sàng</h3>
                    <p>{channelData?.full_name || channelData?.user_id || 'Tài khoản'} đã được thêm vào công ty hiện tại.</p>
                    <Button type="primary" size="large" onClick={onCancel}>Đóng</Button>
                </div>
            );
        }

        return (
            <div className="zalo-connect-state is-error" aria-live="assertive">
                <div className="zalo-connect-state__icon"><CloseCircleFilled /></div>
                <span className="zalo-connect-section-label">Chưa thể kết nối</span>
                <h3>{error === 'Mã QR đã hết hạn' ? 'Mã QR đã hết hạn' : 'Kết nối không thành công'}</h3>
                <p>{error || 'Hãy kiểm tra kết nối mạng và thử tạo lại mã QR.'}</p>
                <div className="zalo-connect-state__actions">
                    <Button type="primary" size="large" icon={<ReloadOutlined />} onClick={initQRLogin}>Tạo mã mới</Button>
                    <Button size="large" onClick={onCancel}>Đóng</Button>
                </div>
            </div>
        );
    };

    return (
        <div className="zalo-connect">
            <header className="zalo-connect-header">
                <div className="zalo-connect-brand">
                    <img src="/zalo.webp" alt="Zalo" />
                </div>
                <div className="zalo-connect-heading">
                    <span>{reconnectChannelId ? 'Kết nối lại tài khoản' : 'Thêm kênh nhắn tin'}</span>
                    <h2>{reconnectChannelId ? `Kết nối lại ${reconnectChannelName || 'Zalo'}` : 'Kết nối tài khoản Zalo'}</h2>
                    <p>Quét mã QR để đồng bộ hội thoại với công ty hiện tại.</p>
                </div>
                <div className="zalo-connect-secure"><LockOutlined /> Bảo mật</div>
            </header>

            {renderProgress()}

            <main className="zalo-connect-body">
                {renderStepContent()}
            </main>
        </div>
    );
};

export default ZaloConnect;
